import {
  parseCalculatorInspectorProjectionV1,
} from "./calculator-layer-inspector-contract.js";
import { parseCalculatorCompactModelBundleV1 } from "./calculator-compact-model.js";
import {
  validateCalculatorScenarioV1,
  validateCalculatorScenarioV2,
} from "./calculator-scenario-contract.js";

export const CALCULATOR_ENGINE_SCHEMA_VERSION = 1;
export const CALCULATOR_ENGINE_STATUSES = Object.freeze([
  "loading",
  "local",
  "validating",
  "validated",
  "stale",
  "failed",
]);

const ISSUE_CODES = new Set([
  "schemaVersionMismatch",
  "engineVersionMismatch",
  "modelRevisionMismatch",
  "invalidScenario",
  "invalidUserLayer",
  "invalidFact",
  "unavailableFact",
]);

function contractError(path, message) {
  return new TypeError(`calculator engine contract failed at ${path}: ${message}`);
}

function requireRecord(value, path) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw contractError(path, "expected an object");
  }
  return value;
}

function requireExactFields(value, fields, path) {
  const object = requireRecord(value, path);
  const expected = [...fields].sort();
  const actual = Object.keys(object).sort();
  if (
    actual.length !== expected.length
    || actual.some((field, index) => field !== expected[index])
  ) {
    throw contractError(path, `fields must be exactly: ${expected.join(", ")}`);
  }
  return object;
}

function requireSchemaVersion(value, path) {
  if (value !== CALCULATOR_ENGINE_SCHEMA_VERSION) {
    throw contractError(path, "expected exactly 1");
  }
}

function requireIdentifier(value, path) {
  if (typeof value !== "string" || value.length === 0 || value.trim() !== value) {
    throw contractError(path, "expected a non-empty identifier without surrounding whitespace");
  }
  return value;
}

function requireNullableIdentifier(value, path) {
  return value === null ? null : requireIdentifier(value, path);
}

function parseContentHash(value, path) {
  const hash = requireExactFields(value, ["algorithm", "digest"], path);
  if (
    (hash.algorithm !== "blake3" && hash.algorithm !== "sha256")
    || typeof hash.digest !== "string"
    || !/^[0-9a-f]{64}$/.test(hash.digest)
  ) {
    throw contractError(path, "expected a supported lowercase 64-character content hash");
  }
  return hash;
}

function parseIssue(value, path) {
  const issue = requireExactFields(value, ["code", "factId", "path"], path);
  if (!ISSUE_CODES.has(issue.code)) {
    throw contractError(`${path}.code`, "unsupported validation issue code");
  }
  if (typeof issue.path !== "string") {
    throw contractError(`${path}.path`, "expected a string");
  }
  requireNullableIdentifier(issue.factId, `${path}.factId`);
  return issue;
}

function parseIssues(value, path) {
  if (!Array.isArray(value)) {
    throw contractError(path, "expected an array");
  }
  value.forEach((issue, index) => parseIssue(issue, `${path}[${index}]`));
  return value;
}

function parseProjectionForRevision(value, revision, engineVersion, path) {
  const projection = parseCalculatorInspectorProjectionV1(value);
  if (projection.modelRevision !== revision) {
    throw contractError(`${path}.modelRevision`, "does not match the response revision");
  }
  if (projection.engineVersion !== engineVersion) {
    throw contractError(`${path}.engineVersion`, "does not match the active engine version");
  }
  return projection;
}

export function parseCalculatorEngineScenarioV1(value) {
  return validateCalculatorScenarioV1(value, "$.scenario", {
    requireExactFields,
    fail(path, message) {
      throw contractError(path, message);
    },
  });
}

export function parseCalculatorEngineScenarioV2(value) {
  return validateCalculatorScenarioV2(value, "$.scenario", {
    requireExactFields,
    fail(path, message) {
      throw contractError(path, message);
    },
  });
}

function parseOutputIdV2(value, path) {
  const output = requireRecord(value, path);
  if (output.kind === "summary") {
    requireExactFields(output, ["kind", "metric"], path);
    requireIdentifier(output.metric, `${path}.metric`);
  } else if (output.kind === "stat") {
    requireExactFields(output, ["kind", "stat"], path);
    requireIdentifier(output.stat, `${path}.stat`);
  } else if (output.kind === "timeline") {
    requireExactFields(output, ["kind", "segment"], path);
    requireIdentifier(output.segment, `${path}.segment`);
  } else if (["target", "lootDistribution", "lootSankey", "trade", "provenance"].includes(output.kind)) {
    requireExactFields(output, ["kind"], path);
  } else {
    throw contractError(`${path}.kind`, "unsupported calculator output identifier");
  }
  return output;
}

function parseOutputValidityV2(value, path) {
  if (!Array.isArray(value)) {
    throw contractError(path, "expected an array");
  }
  const seen = new Set();
  value.forEach((entry, index) => {
    const entryPath = `${path}[${index}]`;
    const validity = requireExactFields(
      entry,
      ["dependencies", "outputId", "state"],
      entryPath,
    );
    parseOutputIdV2(validity.outputId, `${entryPath}.outputId`);
    if (!["current", "stale", "invalid", "unavailable"].includes(validity.state)) {
      throw contractError(`${entryPath}.state`, "unsupported output validity state");
    }
    if (!Array.isArray(validity.dependencies) || validity.dependencies.length === 0) {
      throw contractError(`${entryPath}.dependencies`, "expected a non-empty array");
    }
    validity.dependencies.forEach((dependency, dependencyIndex) => {
      requireIdentifier(dependency, `${entryPath}.dependencies[${dependencyIndex}]`);
    });
    const key = JSON.stringify(validity.outputId);
    if (seen.has(key)) {
      throw contractError(`${entryPath}.outputId`, "duplicate output identifier");
    }
    seen.add(key);
  });
  return value;
}

function parseRepairReportV2(value, path) {
  const report = requireExactFields(value, ["repairs"], path);
  if (!Array.isArray(report.repairs)) {
    throw contractError(`${path}.repairs`, "expected an array");
  }
  report.repairs.forEach((repair, index) => {
    const repairPath = `${path}.repairs[${index}]`;
    const kind = requireIdentifier(repair?.kind, `${repairPath}.kind`);
    const fields = {
      catalogPetConvertedToCustom: ["basedOn", "kind", "path"],
      optionRemoved: ["kind", "optionId", "optionKind", "path"],
      customBasedOnRemoved: ["basedOn", "kind", "path"],
      tierReplaced: ["from", "kind", "path", "to"],
      learnedSkillsTruncated: ["kind", "path", "removedOptionIds"],
      packLeaderCleared: ["kind", "path"],
    }[kind];
    if (!fields) {
      throw contractError(`${repairPath}.kind`, "unsupported catalog repair");
    }
    requireExactFields(repair, fields, repairPath);
    if (typeof repair.path !== "string" || repair.path.length === 0) {
      throw contractError(`${repairPath}.path`, "expected a field path");
    }
    if (kind === "catalogPetConvertedToCustom") {
      requireNullableIdentifier(repair.basedOn, `${repairPath}.basedOn`);
    } else if (kind === "customBasedOnRemoved") {
      requireIdentifier(repair.basedOn, `${repairPath}.basedOn`);
    } else if (kind === "tierReplaced") {
      if (!Number.isInteger(repair.from) || !Number.isInteger(repair.to)) {
        throw contractError(repairPath, "expected integer tier replacement");
      }
    } else if (kind === "optionRemoved") {
      requireIdentifier(repair.optionId, `${repairPath}.optionId`);
      requireIdentifier(repair.optionKind, `${repairPath}.optionKind`);
    } else if (kind === "learnedSkillsTruncated") {
      if (!Array.isArray(repair.removedOptionIds)) {
        throw contractError(`${repairPath}.removedOptionIds`, "expected an array");
      }
      repair.removedOptionIds.forEach((optionId, optionIndex) => {
        requireIdentifier(optionId, `${repairPath}.removedOptionIds[${optionIndex}]`);
      });
    }
  });
  return report;
}

function parseRejectedIssuesV2(value, path) {
  if (!Array.isArray(value) || value.length === 0) {
    throw contractError(path, "expected at least one issue");
  }
  value.forEach((entry, index) => {
    const entryPath = `${path}[${index}]`;
    const fields = ["path", "reason", "severity"];
    if (Object.hasOwn(entry, "message")) fields.push("message");
    const issue = requireExactFields(entry, fields, entryPath);
    if (Object.hasOwn(issue, "message") && (typeof issue.message !== "string"
      || issue.message.length === 0 || issue.message.length > 8192)) {
      throw contractError(`${entryPath}.message`, "expected a bounded diagnostic message");
    }
    if (typeof issue.path !== "string" || issue.path.length === 0) {
      throw contractError(`${entryPath}.path`, "expected a field path");
    }
    requireIdentifier(issue.reason, `${entryPath}.reason`);
    if (issue.severity !== "invalid" && issue.severity !== "unavailable") {
      throw contractError(`${entryPath}.severity`, "expected invalid or unavailable");
    }
  });
  return value;
}

export function parseCalculatorEngineInitialModelDocumentV2(value) {
  const document = requireExactFields(
    value,
    [
      "catalogRevision",
      "confirmation",
      "model",
      "normalizedScenario",
      "outputValidity",
      "repairReport",
      "requestId",
      "schemaVersion",
    ],
    "$.initialModelDocument",
  );
  requireSchemaVersion(document.schemaVersion, "$.initialModelDocument.schemaVersion");
  requireIdentifier(document.requestId, "$.initialModelDocument.requestId");
  requireIdentifier(document.catalogRevision, "$.initialModelDocument.catalogRevision");
  parseCalculatorEngineScenarioV2(document.normalizedScenario);
  parseRepairReportV2(document.repairReport, "$.initialModelDocument.repairReport");
  parseOutputValidityV2(document.outputValidity, "$.initialModelDocument.outputValidity");
  const engineDocument = parseCalculatorEmbeddedEngineDocument({
    schemaVersion: document.schemaVersion,
    requestId: document.requestId,
    model: document.model,
    confirmation: document.confirmation,
  });
  if (engineDocument.model.resolvedRevision !== document.catalogRevision) {
    throw contractError(
      "$.initialModelDocument.catalogRevision",
      "does not match the embedded model revision",
    );
  }
  return document;
}

export function parseCalculatorEngineModelResponseV2(value) {
  const response = requireRecord(value, "$.modelResponse");
  if (response.outcome === "ready" || response.outcome === "recovered") {
    const fields = [
      "catalogRevision",
      "model",
      "normalizedScenario",
      "outcome",
      "outputValidity",
      "projection",
      "requestId",
      "schemaVersion",
    ];
    if (response.outcome === "recovered") {
      fields.push("repairReport");
    }
    requireExactFields(response, fields, "$.modelResponse");
    requireSchemaVersion(response.schemaVersion, "$.modelResponse.schemaVersion");
    requireIdentifier(response.requestId, "$.modelResponse.requestId");
    requireIdentifier(response.catalogRevision, "$.modelResponse.catalogRevision");
    const model = parseCalculatorCompactModelBundleV1(response.model);
    if (model.resolvedRevision !== response.catalogRevision) {
      throw contractError("$.modelResponse.catalogRevision", "does not match the model");
    }
    parseCalculatorEngineScenarioV2(response.normalizedScenario);
    parseCalculatorInspectorProjectionV1(response.projection);
    parseOutputValidityV2(response.outputValidity, "$.modelResponse.outputValidity");
    if (response.outcome === "recovered") {
      parseRepairReportV2(response.repairReport, "$.modelResponse.repairReport");
      if (response.repairReport.repairs.length === 0) {
        throw contractError("$.modelResponse.repairReport", "recovered requires a repair");
      }
    }
    return response;
  }
  if (response.outcome === "rejected") {
    requireExactFields(
      response,
      ["affectedOutputs", "issues", "outcome", "requestId", "schemaVersion"],
      "$.modelResponse",
    );
    requireSchemaVersion(response.schemaVersion, "$.modelResponse.schemaVersion");
    requireIdentifier(response.requestId, "$.modelResponse.requestId");
    parseRejectedIssuesV2(response.issues, "$.modelResponse.issues");
    if (!Array.isArray(response.affectedOutputs)) {
      throw contractError("$.modelResponse.affectedOutputs", "expected an array");
    }
    response.affectedOutputs.forEach((outputId, index) => {
      parseOutputIdV2(outputId, `$.modelResponse.affectedOutputs[${index}]`);
    });
    return response;
  }
  throw contractError("$.modelResponse.outcome", "expected ready, recovered, or rejected");
}

function parseCalculatorEmbeddedEngineDocument(value) {
  const document = requireExactFields(
    value,
    ["confirmation", "model", "requestId", "schemaVersion"],
    "$.initialModelDocument",
  );
  requireSchemaVersion(document.schemaVersion, "$.initialModelDocument.schemaVersion");
  requireIdentifier(document.requestId, "$.initialModelDocument.requestId");
  const model = parseCalculatorCompactModelBundleV1(document.model);
  const confirmation = requireExactFields(
    document.confirmation,
    ["engineVersion", "modelRevision", "userLayerContentHash"],
    "$.initialModelDocument.confirmation",
  );
  requireIdentifier(
    confirmation.engineVersion,
    "$.initialModelDocument.confirmation.engineVersion",
  );
  requireIdentifier(
    confirmation.modelRevision,
    "$.initialModelDocument.confirmation.modelRevision",
  );
  parseContentHash(
    confirmation.userLayerContentHash,
    "$.initialModelDocument.confirmation.userLayerContentHash",
  );
  if (confirmation.engineVersion !== model.engineVersion) {
    throw contractError(
      "$.initialModelDocument.confirmation.engineVersion",
      "does not match the embedded model",
    );
  }
  if (confirmation.modelRevision !== model.modelRevision) {
    throw contractError(
      "$.initialModelDocument.confirmation.modelRevision",
      "does not match the embedded model",
    );
  }
  return document;
}

export function parseCalculatorEngineValidationResponseV1(value, expectedEngineVersion) {
  requireIdentifier(expectedEngineVersion, "$.validationResponse.expectedEngineVersion");
  const response = requireRecord(value, "$.validationResponse");
  if (response.outcome === "validated") {
    requireExactFields(
      response,
      ["modelRevision", "outcome", "projection", "requestId", "schemaVersion"],
      "$.validationResponse",
    );
    requireSchemaVersion(response.schemaVersion, "$.validationResponse.schemaVersion");
    requireIdentifier(response.requestId, "$.validationResponse.requestId");
    requireIdentifier(response.modelRevision, "$.validationResponse.modelRevision");
    parseProjectionForRevision(
      response.projection,
      response.modelRevision,
      expectedEngineVersion,
      "$.validationResponse.projection",
    );
    return response;
  }

  if (response.outcome === "stale") {
    requireExactFields(
      response,
      [
        "confirmedModelRevision",
        "confirmedProjection",
        "issues",
        "outcome",
        "requestId",
        "requestedModelRevision",
        "schemaVersion",
      ],
      "$.validationResponse",
    );
    requireSchemaVersion(response.schemaVersion, "$.validationResponse.schemaVersion");
    requireIdentifier(response.requestId, "$.validationResponse.requestId");
    requireIdentifier(
      response.requestedModelRevision,
      "$.validationResponse.requestedModelRevision",
    );
    requireIdentifier(
      response.confirmedModelRevision,
      "$.validationResponse.confirmedModelRevision",
    );
    parseProjectionForRevision(
      response.confirmedProjection,
      response.confirmedModelRevision,
      expectedEngineVersion,
      "$.validationResponse.confirmedProjection",
    );
    parseIssues(response.issues, "$.validationResponse.issues");
    return response;
  }

  if (response.outcome === "failed") {
    requireExactFields(
      response,
      [
        "confirmedProjection",
        "issues",
        "modelRevision",
        "outcome",
        "requestId",
        "schemaVersion",
      ],
      "$.validationResponse",
    );
    requireSchemaVersion(response.schemaVersion, "$.validationResponse.schemaVersion");
    requireIdentifier(response.requestId, "$.validationResponse.requestId");
    requireNullableIdentifier(response.modelRevision, "$.validationResponse.modelRevision");
    if (response.confirmedProjection !== null) {
      const projection = parseCalculatorInspectorProjectionV1(response.confirmedProjection);
      if (projection.engineVersion !== expectedEngineVersion) {
        throw contractError(
          "$.validationResponse.confirmedProjection.engineVersion",
          "does not match the active engine version",
        );
      }
      if (
        response.modelRevision !== null
        && projection.modelRevision !== response.modelRevision
      ) {
        throw contractError(
          "$.validationResponse.confirmedProjection.modelRevision",
          "does not match modelRevision",
        );
      }
    }
    parseIssues(response.issues, "$.validationResponse.issues");
    return response;
  }

  throw contractError(
    "$.validationResponse.outcome",
    "expected validated, stale, or failed",
  );
}
